import type { SelectOption, SelectOptions } from '@library/types/utils';
import { props, uniq } from 'ramda';
import { DateTime } from 'luxon';
import type { FilterKey } from '@library/types/filters';
import type { LeagueGames } from '@library/types/games';
import type { LeagueProjectionAttributes } from '@library/types/leagues';
import type { Status } from '@library/types/projection';
import { formatWithTimezoneChange } from '@library/utils/dateTime';
import { orderGames } from '@library/utils';

export const SELECT_ALL = '1A';
export const NLS = 'NLS';

export const gameStatusOptions: SelectOptions = [
  {
    key: 'pre_game',
    name: 'Pre Game',
    value: 'pre_game'
  },
  {
    key: 'in_progress',
    name: 'In Progress',
    value: 'in_progress'
  },
  {
    key: 'finished',
    name: 'Finished',
    value: 'finished'
  },
  {
    key: 'removed',
    name: 'Removed',
    value: 'removed'
  },
  {
    key: 'suspended',
    name: 'Suspended',
    value: 'suspended'
  }
];

export const getGameOptions = ({
  games,
  nlsGameOption
}: {
  games: LeagueGames;
  nlsGameOption?: boolean;
}): {
  gameOptions: SelectOptions;
  gameValues: LeagueGames;
} => {
  const sortedGames = orderGames(games);

  const gameOptions: SelectOptions = sortedGames.map((game) => {
    const {
      away_team: awayTeam,
      date_time: dateTime,
      display,
      game_id: gameId,
      home_team: homeTeam
    } = game;

    const gameName =
      awayTeam && homeTeam
        ? `${awayTeam} @ ${homeTeam}`
        : display.substring(0, display.indexOf('@'));

    if (!dateTime) {
      return {
        key: gameId,
        name: display,
        value: gameId
      };
    }

    const dateString = formatWithTimezoneChange(
      DateTime.fromISO(dateTime),
      'D t ZZZZ'
    );
    const gameNameWithTimeZone = `${gameName} ${dateString}`;

    return {
      key: gameId,
      name: gameNameWithTimeZone,
      value: gameId
    };
  });

  const nlsDisplayName = gameOptions.length
    ? 'Other Game'
    : 'Non-Livescored Projections';

  const nlsSelectOption: SelectOption = {
    display: nlsDisplayName,
    key: NLS,
    name: nlsDisplayName,
    value: NLS,
    allOrExclusive: true
  };

  return {
    gameOptions: nlsGameOption
      ? [...gameOptions, nlsSelectOption]
      : gameOptions,
    gameValues: sortedGames
  };
};

export const getGamesTeamOptions = ({
  games,
  selectedGameIds
}: {
  games: LeagueGames;
  selectedGameIds?: Array<string>;
}): SelectOptions => {
  const shouldFilterBySelectedGames = !!selectedGameIds?.length;

  const selectedGames = shouldFilterBySelectedGames
    ? selectedGameIds.flatMap((gameId) => {
        const selectedGame = games.find((game) => game.game_id === gameId);

        return selectedGame ? [selectedGame] : [];
      })
    : games;

  const uniqueGamesTeamList = uniq(
    selectedGames?.flatMap((game) => props(['home_team', 'away_team'], game))
  )
    .filter(Boolean)
    .sort();

  const gamesTeamOptions = uniqueGamesTeamList
    .filter(
      (team: string | string[] | null | undefined): team is string =>
        !!(team as string)
    )
    .map((team) => ({
      key: team,
      name: team,
      value: team
    }));

  return gamesTeamOptions;
};

export const convertLeagueProjectionAttributesToSelectOptions = (
  items: Array<LeagueProjectionAttributes>,
  literalValueNeeded?: boolean
): SelectOptions =>
  items.map((item) => {
    const { leagueProjectionFilterId: id, leagueProjectionFilterName: name } =
      item;

    return {
      key: id,
      name,
      value: literalValueNeeded ? name : id
    };
  });

export const getFilterDisplayCopy = (
  filterKey: FilterKey
): { display: string; value: string } | undefined => {
  switch (filterKey) {
    case 'status':
      return { display: 'Game Status', value: 'Statuses' };
    case 'games':
      return { display: 'Games', value: 'Games' };
    case 'statType':
      return { display: 'Stats', value: 'Stats' };
    case 'teams':
      return { display: 'Teams', value: 'Teams' };
    case 'depositStatus':
      return { display: 'Deposit Statuses', value: 'Deposit Statuses' };
    case 'depositMethodType':
      return { display: 'Deposit Methods', value: 'Deposit Methods' };
    case 'withdrawalStatus':
      return { display: 'Withdrawal Statuses', value: 'Withdrawal Statuses' };
    case 'withdrawalMethod':
      return { display: 'Withdrawal Methods', value: 'Withdrawal Methods' };
    default:
      return undefined;
  }
};

export const getAllOption = (
  label: string,
  isDefaultEmpty?: boolean,
  values?: Array<string>
): SelectOption => ({
  key: SELECT_ALL,
  name: `${isDefaultEmpty && !values?.length ? 'No' : 'All'} ${label}`,
  value: SELECT_ALL
});

export const getBgColor = ({
  open,
  disabled
}: {
  open: boolean;
  disabled: boolean;
}) => {
  if (open) {
    return 'bg-soFresh-120';
  }
  if (disabled) {
    return 'bg-soFresh-130';
  }
  return 'bg-soFresh-110';
};

export const getPositionOptions = (
  items: Array<LeagueProjectionAttributes>
): SelectOptions =>
  items.map((item) => {
    const { leagueProjectionFilterId: id, leagueProjectionFilterName: name } =
      item;

    return {
      key: id,
      name,
      value: name
    };
  });

export const getParameterValue = <T = string>(
  value: string | Array<string>
): T => {
  if (typeof value === 'string') {
    return value === SELECT_ALL ? ('' as T) : (value as T);
  }
  if (Array.isArray(value)) {
    if (value.length > 1) {
      return value.join() as T;
    }
    return ![SELECT_ALL, 'NLS'].includes(value[0])
      ? (value[0] as T)
      : ('' as T);
  }

  return value;
};

export const getBooleanParameterValue = (
  booleanValue: string
): boolean | undefined => {
  if (booleanValue === SELECT_ALL || booleanValue === '') {
    return undefined;
  }

  return booleanValue === 'true';
};

export const getStatusNotFinished = (selectedStatus: string): Array<Status> => {
  const status = getParameterValue<Array<Status>>([selectedStatus]);

  return status.length > 0
    ? status
    : ['removed', 'pre_game', 'in_progress', 'suspended'];
};

export * from '@library/component-utils/paymentsfilters';
